import { Fragment, useRef, type KeyboardEvent, type MouseEvent } from "react";
import { Tooltip, VisuallyHidden } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import { useBulkSelectionStore, useChatsTabs } from "@/features/chat/stores";
import { useSectorConnection } from "@/core/providers/SectorConnectionProvider/context";
import type {
  StatusTabValue,
  TabValue,
} from "@/features/chat/stores/useChatsTabs";
import {
  CHAT_TABS,
  CHAT_TAB_LABELS,
  CHAT_TABPANEL_ID,
  chatTabId,
  tabCountTooltip,
} from "@/features/chat/utils/chatTabs";
import classes from "./ChatTabs.module.css";

export type ChatTabCounts = Record<StatusTabValue, number | null>;

type StripTab = {
  value: StatusTabValue;
  count?: number | null;
  /** Motivo do bloqueio (tooltip); com ele a aba não troca. */
  disabledReason?: string;
  /** Aba da IA travada por falta de agente: o clique abre a configuração. */
  lockedByAssistant?: boolean;
};

type ChatTabsStripProps = {
  tabs: StripTab[];
  activeTab: TabValue;
  disabled?: boolean;
  onChange?: (tab: StatusTabValue) => void;
  /** `id` do painel da lista (`aria-controls`); o esqueleto não tem painel. */
  panelId?: string;
};

/**
 * Na lista estreita o rótulo encurta com reticências: o nome inteiro vai no
 * `title`, só quando cortado (inteiro, a dica nativa só repetiria o texto).
 */
const titleWhenTruncated = (event: MouseEvent<HTMLSpanElement>) => {
  const el = event.currentTarget;
  el.title = el.scrollWidth > el.clientWidth ? (el.textContent ?? "") : "";
};

/**
 * Faixa das três abas, só apresentação: o esqueleto da página usa a mesma
 * faixa (sem contadores) para não trocar de forma quando a tela carrega.
 *
 * Padrão de abas do WAI-ARIA: só a aba ativa entra no Tab (roving tabindex);
 * setas esquerda/direita, Home e End andam entre as abas que podem ser
 * escolhidas e já ativam a aba (seleção segue o foco).
 */
export const ChatTabsStrip = ({
  tabs,
  activeTab,
  disabled = false,
  onChange,
  panelId,
}: ChatTabsStripProps) => {
  const tabRefs = useRef<
    Partial<Record<StatusTabValue, HTMLButtonElement | null>>
  >({});
  const isTabDisabled = (tab: StripTab) =>
    disabled || Boolean(tab.disabledReason);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const enabled = tabs.filter((tab) => !isTabDisabled(tab));
    if (enabled.length === 0) return;
    const current = enabled.findIndex((tab) => tab.value === activeTab);
    let next: number;
    switch (event.key) {
      case "ArrowRight":
        next = (current + 1) % enabled.length;
        break;
      case "ArrowLeft":
        next = (current - 1 + enabled.length) % enabled.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = enabled.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const target = enabled[next].value;
    tabRefs.current[target]?.focus();
    onChange?.(target);
  };

  return (
    <div className={classes.strip}>
      <div
        className={classes.list}
        role="tablist"
        aria-label="Conversas">
        {tabs.map((tab) => {
          const isActive = tab.value === activeTab;
          const isDisabled = isTabDisabled(tab);
          const label = CHAT_TAB_LABELS[tab.value];
          const count = tab.count ?? null;
          const showCount = count !== null && !isDisabled;
          const countHint =
            count !== null ? tabCountTooltip(tab.value, count) : "";
          const hintId = `${chatTabId(tab.value)}-count`;

          const button = (
            <button
              ref={(el) => {
                tabRefs.current[tab.value] = el;
              }}
              type="button"
              role="tab"
              id={chatTabId(tab.value)}
              className={classes.tab}
              aria-selected={isActive}
              aria-controls={panelId}
              aria-disabled={isDisabled || undefined}
              aria-describedby={showCount ? hintId : undefined}
              tabIndex={isActive ? 0 : -1}
              data-active={isActive || undefined}
              data-disabled={isDisabled || undefined}
              data-locked-by-assistant={
                tab.lockedByAssistant && !disabled ? "true" : undefined
              }
              onKeyDown={handleKeyDown}
              onClick={() => {
                if (!isDisabled) onChange?.(tab.value);
              }}>
              <span
                className={classes.label}
                onMouseEnter={
                  tab.disabledReason ? undefined : titleWhenTruncated
                }>
                {label}
              </span>
              {tab.lockedByAssistant && !disabled && (
                <IconLock
                  size={13}
                  aria-hidden
                  className={classes.lockIcon}
                />
              )}
              {showCount && (
                <>
                  <Tooltip
                    label={countHint}
                    withArrow>
                    <span
                      className={classes.count}
                      aria-hidden>
                      {count}
                    </span>
                  </Tooltip>
                  {/* O leitor de tela ouve o contador pela descrição da aba;
                      o tooltip do número só abre no mouse. */}
                  <VisuallyHidden id={hintId}>{countHint}</VisuallyHidden>
                </>
              )}
            </button>
          );

          return tab.disabledReason && !disabled ? (
            <Tooltip
              key={tab.value}
              label={tab.disabledReason}
              withArrow
              multiline
              maw={260}>
              {button}
            </Tooltip>
          ) : (
            <Fragment key={tab.value}>{button}</Fragment>
          );
        })}
      </div>
    </div>
  );
};

interface ChatTabsProps {
  /** Total de cada aba (`pagination.total` da 1ª página); `null` carregando. */
  counts: ChatTabCounts;
  agentIsActive?: boolean;
  sectorHasCustomWebhook?: boolean;
}

const ChatTabs = ({
  counts,
  agentIsActive,
  sectorHasCustomWebhook,
}: ChatTabsProps) => {
  const activeTab = useChatsTabs((s) => s.activeTab);
  const setActiveTab = useChatsTabs((s) => s.setActiveTab);
  const { connected } = useSectorConnection();
  const isBulkActive = useBulkSelectionStore((s) => s.mode !== "off");
  const assistantLocked =
    agentIsActive === false && sectorHasCustomWebhook === false;

  const tabs: StripTab[] = CHAT_TABS.map((value) => {
    if (value !== "ai") return { value, count: counts[value] };
    // A IA não entra no encerramento em massa: com a seleção aberta, a aba
    // dela fica travada (a seleção vale só entre as abas humanas).
    const disabledReason = assistantLocked
      ? "Para desbloquear a aba Com a IA, configure seu Agente de IA"
      : isBulkActive
        ? "Saia do modo seleção para trocar de aba"
        : undefined;
    return {
      value,
      count: counts[value],
      disabledReason,
      lockedByAssistant: assistantLocked,
    };
  });

  return (
    <ChatTabsStrip
      tabs={tabs}
      activeTab={activeTab}
      disabled={!connected}
      onChange={setActiveTab}
      panelId={CHAT_TABPANEL_ID}
    />
  );
};

export default ChatTabs;
